import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Snackbar, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Capacitor } from '@capacitor/core';

const DISMISS_KEY_PREFIX = 'cm_open_in_app_dismissed_';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.circlemoments.app';
const FALLBACK_DELAY_MS = 1200;

// Shareable-link routes that Android's App Link intent-filter also covers
// (see AndroidManifest.xml) - kept in sync with that filter's pathPrefixes.
const ELIGIBLE_PREFIXES = ['/join-event', '/event-deletion-request', '/share', '/upload'];

function isEligiblePath(pathname) {
  return ELIGIBLE_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function isAndroidBrowser() {
  return /Android/i.test(window.navigator.userAgent || '');
}

function buildDeepLink(pathname, search) {
  return `circlemoments://open${pathname}${search}`;
}

// Web-only fallback for opening a join-event/share/upload link in the native
// app when the OS-level App Link hand-off doesn't fire - most commonly
// because the link was tapped inside an in-app browser (WhatsApp/Instagram/
// Messenger), which routes navigation through its own webview instead of
// giving Android's link resolver a chance. circlemoments:// is a plain
// custom-scheme intent (see AndroidManifest.xml's second intent-filter), so
// any Android webview resolves it as "open the app" regardless of domain
// verification. Renders nothing on iOS/desktop/inside the native app itself.
const OpenInAppBanner = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const dismissKeyRef = useRef('');

  const eligible = !Capacitor.isNativePlatform() && isAndroidBrowser() && isEligiblePath(location.pathname);

  const attemptOpen = useCallback((onStillHere) => {
    window.location.href = buildDeepLink(location.pathname, location.search);
    if (!onStillHere) return;
    window.setTimeout(() => {
      if (document.visibilityState !== 'hidden') onStillHere();
    }, FALLBACK_DELAY_MS);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setOpen(false);
    if (!eligible) return undefined;

    const dismissKey = `${DISMISS_KEY_PREFIX}${location.pathname}`;
    dismissKeyRef.current = dismissKey;
    if (sessionStorage.getItem(dismissKey) === '1') return undefined;

    let cancelled = false;
    attemptOpen(() => { if (!cancelled) setOpen(true); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, location.pathname]);

  const dismiss = useCallback(() => {
    setOpen(false);
    if (dismissKeyRef.current) sessionStorage.setItem(dismissKeyRef.current, '1');
  }, []);

  const handleOpenClick = useCallback(() => {
    dismiss();
    attemptOpen(() => { window.location.href = PLAY_STORE_URL; });
  }, [dismiss, attemptOpen]);

  if (!eligible) return null;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        top: 'calc(12px + env(safe-area-inset-top))',
        maxWidth: 'none',
        width: { xs: 'calc(100% - 32px)', sm: 'auto' }
      }}
      ContentProps={{
        sx: { flexWrap: 'nowrap', alignItems: 'center', columnGap: 1, width: '100%' }
      }}
      message={
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Open this in the CircleMoments app?
        </span>
      }
      action={
        <>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleOpenClick}
            sx={{
              fontWeight: 600,
              flexShrink: 0,
              borderColor: 'primary.light',
              color: 'primary.light',
              '&:hover': { borderColor: 'primary.light', backgroundColor: 'rgba(255,255,255,0.08)' }
            }}
          >
            Open
          </Button>
          <IconButton size="small" color="inherit" onClick={dismiss} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
    />
  );
};

export default OpenInAppBanner;
